export type SavedReceiptSnapshot = {
    transactionNo: string;
    customerCode: string;
    customerName: string;
    userName: string;
    locationLabel: string;
    paymentModeLabel: string;
    chequeNo: string;
    chequeDate: string;
    payerName: string;
    referenceNo: string;
    note: string;
    amount: number;
    transactionDate: string;
    companyName: string;
    companyAddress: string;
};

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

// Opens the browser print dialog for a customer receipt. Returns false when the
// print window was blocked.
export const printCustomerReceipt = (receipt: SavedReceiptSnapshot): boolean => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
        return false;
    }

    const now = new Date();
    const printDate = now.toLocaleDateString();
    const printTime = now.toLocaleTimeString();
    const isCheque = Boolean(receipt.chequeNo);
    const secondColumnLabel = isCheque ? "Cheque No" : "Payment Mode";
    const secondColumnValue = isCheque ? receipt.chequeNo : receipt.paymentModeLabel || "-";

    const field = (label: string, value: string): string => `
        <div class="field-row"><span class="field-label">${escapeHtml(label)}</span><span>: ${escapeHtml(value || "-")}</span></div>`;

    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Customer Receipt</title>
            <style>
                @page { margin: 8mm; }
                * { box-sizing: border-box; }
                html, body { margin: 0; }
                /* Content padding on top of @page margin: printers and the "Margins: None"
                   dialog option can drop @page margins and clip the edges. */
                body { font-family: Arial, sans-serif; padding: 4mm 6mm; color: #1a1a1a; font-size: 13px; overflow-wrap: anywhere; }
                .header-row { display: flex; justify-content: space-between; align-items: flex-start; }
                .header-left div { margin-bottom: 2px; }
                .header-left .company-name { font-weight: 700; font-size: 15px; }
                .header-left .title { font-weight: 700; font-size: 15px; margin-top: 6px; }
                .header-right { text-align: right; white-space: nowrap; }
                .header-right div { margin-bottom: 2px; }
                hr { border: none; border-top: 1px solid #333; margin: 12px 0; }
                .two-col { display: flex; justify-content: space-between; }
                .field-row { display: flex; margin-bottom: 6px; }
                .field-label { width: 110px; flex-shrink: 0; }
                .three-col { display: flex; gap: 24px; }
                .three-col > div { min-width: 0; }
                .col-info { flex: 1.5; }
                .col-payment { flex: 1.2; }
                .col-note { flex: 0.8; margin-left: 12px; }
                .note-box { border: 1px solid #333; width: 140px; height: 70px; padding: 6px; margin-top: 4px; }
                .signature-row { display: flex; justify-content: space-between; margin-top: 70px; }
                .signature-line { border-top: 1px dotted #333; width: 220px; text-align: center; padding-top: 4px; }
            </style>
        </head>
        <body>
            <div>
                <div class="header-row">
                    <div class="header-left">
                        <div class="company-name">${escapeHtml(receipt.companyName || "-")}</div>
                        <div>${escapeHtml(receipt.companyAddress || "-")}</div>
                        <div class="title">Customer Receipt</div>
                    </div>
                    <div class="header-right">
                        <div>Page No: 1</div>
                        <div>Print Date: ${escapeHtml(printDate)}</div>
                        <div>Print Time: ${escapeHtml(printTime)}</div>
                    </div>
                </div>
                <hr />
                <div class="two-col">
                    <div>
                        ${field("Customer code", receipt.customerCode)}
                        ${field("Customer name", receipt.customerName)}
                    </div>
                    <div>
                        ${field("User name", receipt.userName)}
                    </div>
                </div>
                <hr />
                <div class="three-col">
                    <div class="col-info">
                        ${field("Date", receipt.transactionDate)}
                        ${field("Transaction No", receipt.transactionNo)}
                        ${field("Location", receipt.locationLabel)}
                        ${field("Reference No", receipt.referenceNo)}
                    </div>
                    <div class="col-payment">
                        ${field(secondColumnLabel, secondColumnValue)}
                        ${field("Payer Name", receipt.payerName)}
                        ${field("Cheque Date", receipt.chequeDate)}
                        ${field("Amount", receipt.amount.toFixed(2))}
                    </div>
                    <div class="col-note">
                        <div>Note</div>
                        <div class="note-box">${escapeHtml(receipt.note)}</div>
                    </div>
                </div>
                <hr />
                <div class="signature-row">
                    <div class="signature-line">Customer Signature</div>
                    <div class="signature-line">Manager Signature</div>
                </div>
            </div>
        </body>
        </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
    };

    return true;
};
